import React, { useEffect, useRef, useState } from 'react';

// Instagram post images - Replace these with actual Instagram image URLs
const INSTAGRAM_IMAGES = [
  { id: 1, url: '/images/instagram-1.heic', link: 'https://www.instagram.com/p/DCT_x0bvwZk/' },
  { id: 2, url: '/images/instagram-2.mp4', link: 'https://www.instagram.com/p/DCLJ7jVhhq8/' },
  { id: 3, url: '/images/instagram-3.mp4', link: 'https://www.instagram.com/p/Ct9ezQWNe4S/' },
  { id: 4, url: '/images/instagram-4.jpg', link: 'https://www.instagram.com/tv/CY4psOplZHy/' },
  { id: 5, url: '/images/instagram-5.jpg', link: 'https://www.instagram.com/tv/CY4pmXUFwbt/' },
  { id: 6, url: '/images/instagram-6.mp4', link: 'https://www.instagram.com/p/CTHCLErB_h7/' },
  { id: 7, url: '/images/instagram-7.jpg', link: 'https://www.instagram.com/p/CQETSS5BNtE/' },
  { id: 8, url: '/images/instagram-8.jpg', link: 'https://www.instagram.com/p/CTaGYzIBS-q/' },
];

const InstagramGallery = () => {
  const scrollContainerRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);
  const autoScrollRef = useRef(null);

  // Auto-scroll functionality
  useEffect(() => {
    if (!isHovering && scrollContainerRef.current) {
      autoScrollRef.current = setInterval(() => {
        const container = scrollContainerRef.current;
        if (container) {
          // Scroll by one image width (320px + gap)
          const scrollAmount = 336; // 320px image + 16px gap

          // Check if we've reached the end
          if (container.scrollLeft + container.clientWidth >= container.scrollWidth - 10) {
            // Reset to beginning
            container.scrollTo({ left: 0, behavior: 'smooth' });
          } else {
            // Scroll forward
            container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
          }
        }
      }, 3000); // Scroll every 3 seconds

      return () => clearInterval(autoScrollRef.current);
    } else {
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
      }
    }
  }, [isHovering]);

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = 336; // 320px image + 16px gap
      const newScrollLeft = direction === 'left'
        ? container.scrollLeft - scrollAmount
        : container.scrollLeft + scrollAmount;

      container.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
    }
  };

  return (
    <section className="bg-[#FFF5F2] py-16 px-6">
      <div className="max-w-screen-xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-gray-900 leading-tight mb-3 sm:mb-4">
            <span className="bg-[#E85D44] text-[#FFD93D] px-4 sm:px-6 py-1 sm:py-2 inline-block">Follow</span> Our Gallery
          </h2>
          <a
            href="https://www.instagram.com/merchm.store/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#E85D44] hover:text-[#d54d34] font-semibold transition-colors"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
            @merchm.store
          </a>
        </div>

        {/* Gallery with Navigation Arrows */}
        <div className="relative">
          {/* Left Arrow */}
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-[#E85D44] rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-300 -ml-4"
            aria-label="Scroll left"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Scrollable Gallery - Hidden scrollbar */}
          <div
            ref={scrollContainerRef}
            className="flex gap-4 overflow-x-auto scroll-smooth py-4"
            style={{
              scrollbarWidth: 'none', // Firefox
              msOverflowStyle: 'none', // IE and Edge
            }}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <style>{`
              /* Hide scrollbar for Chrome, Safari and Opera */
              .overflow-x-auto::-webkit-scrollbar {
                display: none;
              }
            `}</style>

            {INSTAGRAM_IMAGES.map((item) => {
              const isVideo = item.url.endsWith('.mp4');

              return (
                <a
                  key={item.id}
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 w-80 h-80 rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105"
                >
                  {isVideo ? (
                    <video
                      src={item.url}
                      className="w-full h-full object-cover"
                      autoPlay
                      loop
                      muted
                      playsInline
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div class="w-full h-full bg-[#E85D44] flex items-center justify-center text-white">Video</div>';
                      }}
                    />
                  ) : (
                    <img
                      src={item.url}
                      alt={`Instagram post ${item.id}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/320x320/E85D44/FFFFFF?text=Instagram+Post';
                      }}
                    />
                  )}
                </a>
              );
            })}
          </div>

          {/* Right Arrow */}
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-[#E85D44] rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-300 -mr-4"
            aria-label="Scroll right"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
};

export default InstagramGallery;
